import { BuilderBlocks, type BuilderElement } from '@builder.io/react';
import { telemetryIncrementAction } from 'core-events/actions';
import React, { JSX, useContext, useState } from 'react';
import { SearchFilterContentTypes, SearchOption, VideoSearchFilterContentTypes } from 'search-gateway-client';
import { twMerge } from 'tailwind-merge';

import { Button, Text } from '@videoblocks/design-system';

import type { ProminentSearchButtons } from '../configurations/prominentSearchConfig';
import { ConversationalSearchProvider, StatsigExperimentContext } from '../context/NavSearchContext';
import Image from '../elements/ProgressiveImage/ProgressiveImage';
import ProminentSearchContainer from '../elements/ProminentSearchContainer';
import { useSearchWithAI } from '../hooks/useSearchWithAI';
import { BuilderState } from '../types/shared';
import getAssetSource from '../utils/getAssetSource';
import getTrendingTagLinks from '../utils/getTrendingTagLinks';

/**
 * This component also exists in Stockblocks. The two should be kept in sync
 * until it goes away in Stockblocks.
 */

const imagesUrl = process.env.NEXT_PUBLIC_ASSET_PATH ?? '/images';

const eventTrackingForProminentSearch = () => {
    void telemetryIncrementAction('homepagesearch.trending_keyword_click');
};

export const defaultContentTypes: SearchOption[] = [
    {
        id: SearchFilterContentTypes.All_videos_content_type,
        label: 'Video',
        placeholderText: 'Search video library...',
    },
    {
        id: SearchFilterContentTypes.Templates,
        label: 'Video Templates',
        placeholderText: 'Search templates library...',
    },
    {
        id: SearchFilterContentTypes.All_audio_content_type,
        label: 'Audio',
        placeholderText: 'Search audio library...',
    },
    {
        id: SearchFilterContentTypes.All_images_content_type,
        label: 'Images',
        placeholderText: 'Search image library...',
    },
];

export interface Props {
    headingFirstLine?: string;
    headingSecondLine?: string;
    videoSrc: string;
    imageSrc: string;
    posterSrc: string;
    trendingTags?: string;
    contentTypes?: SearchOption[];
    showCallToAction: boolean;
    callToAction?: ProminentSearchButtons;
    showSecondaryCta: boolean;
    secondaryCta?: ProminentSearchButtons;
    builderState: BuilderState;
    socialProof?: {
        logoAlt: string;
        logoImage: string;
    }[];
    extraPaddingBottom: boolean;
    hideSearchBarInMobile: boolean;
    dynamicContent: {
        blocks: React.ReactNode[];
    };
    subheaderDynamicContent?: {
        blocks: React.ReactNode[];
    };
    builderBlock: BuilderElement;
}

const ProminentSearchWrapper = (props: Props): JSX.Element => (
    <ConversationalSearchProvider>
        <ProminentSearch {...props} />
    </ConversationalSearchProvider>
);

function ProminentSearch({
    headingFirstLine = 'Get Unlimited',
    headingSecondLine = 'Stock Media',
    trendingTags = '',
    videoSrc = '',
    imageSrc = '',
    posterSrc = '',
    contentTypes = defaultContentTypes,
    showCallToAction,
    callToAction,
    showSecondaryCta,
    secondaryCta,
    builderState = {},
    socialProof = [],
    extraPaddingBottom,
    hideSearchBarInMobile,
    builderBlock,
    dynamicContent = {
        blocks: [],
    },
    subheaderDynamicContent = {
        blocks: [],
    },
}: Props): JSX.Element {
    const [contentType, setContentType] = useState(contentTypes[0]?.id);
    const { showConversationalSearchCta = false } = useContext(StatsigExperimentContext);
    const { conversationalSearchEnabled, isSearchWithAIEnabledForContentType } = useSearchWithAI({
        showConversationalSearchCta,
        contentType,
    });
    const hasAnyCtaButton = (showCallToAction && callToAction?.href) || (showSecondaryCta && secondaryCta?.href);

    const aiCtaEnabledForContentType = React.useMemo(
        () => isSearchWithAIEnabledForContentType(contentType),
        [contentType, isSearchWithAIEnabledForContentType],
    );

    const isMobile = builderState?.state?.device === 'mobile';
    const isLargeView = builderState?.state?.deviceSize === 'large';
    const numberOfLogos = socialProof.length;

    const containerProminentSearchClass = twMerge('relative', extraPaddingBottom && 'pb-24');

    const isPrimaryCtaEnabled = showCallToAction && (!isLargeView || !callToAction?.mobileOnly);
    const isSecondaryCtaEnabled = showSecondaryCta && (!isLargeView || !secondaryCta?.mobileOnly);

    const { blocks } = dynamicContent;
    const { blocks: subheaderBlocks } = subheaderDynamicContent;

    return (
        <div className={containerProminentSearchClass}>
            {/*Background*/}
            <div className="absolute top-0 left-0 w-full h-full overflow-hidden bg-gray-900">
                <div className="overflow-hidden w-full h-full">
                    <div className="transition-all h-full whitespace-nowrap">
                        {!isMobile && videoSrc && (
                            <video
                                className="hero-video w-full h-full object-cover"
                                poster={getAssetSource(posterSrc)}
                                loop
                                autoPlay
                                muted
                            >
                                <source src={getAssetSource(videoSrc)} type="video/mp4" />
                            </video>
                        )}
                        {isMobile && posterSrc && (
                            <Image
                                className="absolute inset-0 object-cover w-full h-full"
                                src={getAssetSource(posterSrc)}
                                alt=""
                                shouldLazyLoad={true}
                                fill={true}
                            />
                        )}
                        {imageSrc && (!videoSrc || (isMobile && !posterSrc)) && (
                            <Image
                                className="absolute inset-0 object-cover w-full h-full"
                                src={getAssetSource(imageSrc)}
                                alt=""
                                shouldLazyLoad={true}
                                fill={true}
                            />
                        )}
                    </div>
                </div>
            </div>
            {/*Header Container*/}
            <div className="flex w-full relative m-auto justify-center">
                <div
                    className={twMerge(
                        'gap-y-16 max-w-screen-2xl grid grid-cols-12 pt-16 md:pt-24 px-6 md:px-12',
                        showConversationalSearchCta ? 'lg:px-12' : 'lg:px-24',
                        blocks.length === 0 && numberOfLogos === 0 && 'pb-16 md:pb-24',
                    )}
                >
                    {/*Header*/}
                    <div className="justify-center flex flex-col col-span-full px-2 md:px-4 lg:px-4">
                        {/*Header Text*/}
                        <div className="justify-center flex flex-col">
                            <h1 className="font-polysans font-bold text-white mb-0 text-center text-3xl lg:text-4xl">
                                {conversationalSearchEnabled ? (
                                    'Find the perfect video asset with AI Search'
                                ) : (
                                    <>
                                        {headingFirstLine}
                                        <br />
                                        {headingSecondLine}
                                    </>
                                )}
                            </h1>
                            <BuilderBlocks
                                parentElementId={builderBlock.id}
                                dataPath={`subheaderDynamicContent.blocks`}
                                blocks={subheaderBlocks}
                            />
                        </div>
                        {(isLargeView || !hideSearchBarInMobile) && (
                            <>
                                <div id="unifiedNav-search-app" className="prominent-search-bar pt-8">
                                    <ProminentSearchContainer
                                        imagesUrl={imagesUrl}
                                        contentTypes={contentTypes}
                                        contentType={contentType}
                                        setContentType={setContentType}
                                        showDropdown={false}
                                        showContentToggles
                                        aiCtaDisabledForContentType={!aiCtaEnabledForContentType}
                                    />
                                </div>
                                {!conversationalSearchEnabled && trendingTags && (
                                    <div
                                        className={twMerge(
                                            'flex justify-center py-4 md:py-8',
                                            hasAnyCtaButton && 'pb-0 md:pb-0',
                                        )}
                                    >
                                        <Text fontWeight="regular" className="text-white">
                                            Trending:&nbsp;
                                        </Text>
                                        {trendingTags.split(',').map((tag, idx) => (
                                            <a
                                                className="homepage-trending text-white text-sm mobile-md:text-base ppro-sm:text-base md:text-base lg-text-base font-bold underline pr-1 underline-offset-5"
                                                href={getTrendingTagLinks(
                                                    tag.toLowerCase().replace(/\s/g, '-'),
                                                    contentType,
                                                )}
                                                onClick={eventTrackingForProminentSearch}
                                                key={tag}
                                            >
                                                {tag}
                                                {idx === trendingTags.split(',').length - 1 ? '' : ','}
                                            </a>
                                        ))}
                                    </div>
                                )}
                            </>
                        )}
                        {(isPrimaryCtaEnabled || isSecondaryCtaEnabled) && (
                            <div className="flex flex-col items-center sm:flex-row justify-center gap-4 pt-4 md:pt-8 md:gap-4 lg:gap-8">
                                {isPrimaryCtaEnabled && (
                                    <Button
                                        className={twMerge(
                                            'w-[75%] md:w-auto',
                                            callToAction?.mobileOnly && 'lg:hidden',
                                        )}
                                        {...callToAction}
                                    >
                                        {callToAction?.text}
                                    </Button>
                                )}
                                {isSecondaryCtaEnabled && (
                                    <Button
                                        className={twMerge(
                                            'w-[75%] md:w-auto',
                                            secondaryCta?.mobileOnly && 'lg:hidden',
                                        )}
                                        {...secondaryCta}
                                    >
                                        {secondaryCta?.text}
                                    </Button>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <div className={twMerge((blocks.length > 0 || numberOfLogos > 0) && 'pb-16 md:pb-24')}>
                <BuilderBlocks
                    parentElementId={builderBlock.id}
                    dataPath={`dynamicContent.blocks`}
                    blocks={blocks}
                    className={twMerge('w-full relative z-9 m-auto', blocks.length > 0 && 'pt-4 md:pt-8')}
                />
                {numberOfLogos > 0 && (
                    <div className="w-full relative z-9 m-auto md:px-12 lg:px-16 pt-4 md:pt-8">
                        <div
                            className={`flex flex-wrap content-center gap-y-2 max-w-[316px] md:max-w-[672px] lg:max-w-[1248px] m-auto items-center ${numberOfLogos <= 5 ? 'justify-center gap-4' : 'justify-around'}`}
                        >
                            {socialProof.map(({ logoAlt, logoImage }, index) => (
                                <div className="flex-initial" key={`logo_${index}`}>
                                    <img
                                        src={logoImage}
                                        alt={logoAlt}
                                        className=" w-auto h-auto max-h-[32px] max-w-[64px] lg:max-h-[54px] lg:max-w-[108px]"
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default ProminentSearchWrapper;
